import React from 'react';

import { reset } from '@klokhuis/core/Analytics/libs/analytics-lib';
import { useIntl } from '@klokhuis/core/Intl';
import { ROUTES } from '@klokhuis/core/Routing';

import { AuthContext } from './AuthContext';
import { setMemAccessToken } from './token';
import { IAuth, IAuthState } from './types';

export const AuthProvider = ({
  authState,
  children,
}: {
  authState: IAuthState;
  children: React.ReactNode;
}) => {
  const { locale } = useIntl();

  const [localAuthState, setLocalAuthState] =
    React.useState<IAuthState>(authState);
  const [isHydrated, setIsHydrated] = React.useState(false);

  // Safe localStorage access helper
  const safeGetLocalStorage = (key: string): string | null => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return null;
      return window.localStorage.getItem(key);
    } catch (e: any) {
      // Storage access may be blocked in some contexts (iframe, privacy settings, etc.)
      // Silently fail - don't log SecurityError to avoid console noise
      if (e?.name !== 'SecurityError' && !e?.message?.includes('storage')) {
        console.warn('localStorage getItem failed:', e);
      }
      return null;
    }
  };

  const safeRemoveLocalStorage = (key: string): void => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return;
      window.localStorage.removeItem(key);
    } catch (e: any) {
      // Storage access may be blocked in some contexts
      // Silently fail - don't log SecurityError to avoid console noise
      if (e?.name !== 'SecurityError' && !e?.message?.includes('storage')) {
        console.warn('localStorage removeItem failed:', e);
      }
    }
  };

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    // Tokens are now in HTTP-only cookies, not accessible from client-side JS
    // We can still sync userData from localStorage for backward compatibility
    const syncUserData = () => {
      // If server says no accessToken, clear localStorage (cookies were cleared)
      if (!authState.accessToken) {
        setMemAccessToken(null);
        safeRemoveLocalStorage('userData');
        setLocalAuthState({
          accessToken: null,
          refreshToken: null,
          userProfile: null,
          anonymousId: authState.anonymousId,
        });
        return;
      }

      // Populate in-memory token store so cross-domain API calls can use Bearer auth
      setMemAccessToken(authState.accessToken);

      const userDataStr = safeGetLocalStorage('userData');
      let userProfile = null;
      if (userDataStr) {
        try {
          userProfile = JSON.parse(userDataStr);
        } catch (e) {
          console.error('Failed to parse userData from localStorage', e);
        }
      }
      // Use server-provided authState as source of truth for tokens
      setLocalAuthState({
        accessToken: authState.accessToken, // From server (cookie)
        refreshToken: null, // Not accessible from client (HTTP-only cookie)
        userProfile: authState.userProfile || userProfile, // Prefer server-provided userProfile
        anonymousId: authState.anonymousId,
      });
    };

    syncUserData();
    setIsHydrated(true);
  }, [authState]);

  const signout: IAuth['signout'] = React.useCallback(async () => {
    if (typeof window !== 'undefined') {
      // Clear localStorage userData first
      safeRemoveLocalStorage('userData');

      // Clear cookies via API route
      try {
        await fetch('/api/auth/logout', {
          method: 'POST',
          credentials: 'include',
        });
      } catch (error) {
        console.error('Failed to logout via API:', error);
        // Even if API fails, clear cookies manually
        document.cookie =
          'accessToken=; HttpOnly; Path=/; Max-Age=0; SameSite=Lax';
        document.cookie =
          'refreshToken=; HttpOnly; Path=/; Max-Age=0; SameSite=Lax';
      }
    }
    reset();
    window.mixpanel?.reset();
    window.location.assign(ROUTES.Home.url({}));
  }, []);

  const effectiveAuthState = React.useMemo(() => {
    // Prefer server-provided authState (from cookies) over local state
    // Only use local state for userProfile if it's more recent
    if (isHydrated) {
      return {
        ...authState, // Server state (includes accessToken from cookie)
        userProfile: localAuthState.userProfile || authState.userProfile,
      };
    }
    return authState;
  }, [localAuthState, authState, isHydrated]);

  // Proactive token refresh: refresh accessToken before it expires (at 14 minutes)
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isHydrated || !effectiveAuthState.accessToken) return;

    // Set up interval to refresh token at 14 minutes
    // Access token expires at 15 minutes, so refresh at 14 minutes to stay ahead
    let refreshInterval: NodeJS.Timeout | null = null;
    let mounted = true;

    const setupRefresh = async () => {
      try {
        const { refreshAccessToken } = await import(
          '@klokhuis/core/Auth/token'
        );

        // Check if component was unmounted during async import
        if (!mounted) return;

        // Only set interval if component is still mounted
        // This prevents setting up interval if component unmounts during initial setup
        if (mounted) {
          // Refresh at 14 minutes (840000 ms) - before 15 minute expiry
          refreshInterval = setInterval(async () => {
            try {
              // Check mounted status before proceeding
              if (!mounted) return;
              const refreshed = await refreshAccessToken();
              // Check again after async operation completes
              if (!mounted) return;

              if (!refreshed) {
                console.warn(
                  'Token refresh failed, user may need to re-authenticate',
                );
              } else {
                console.log('Access token refreshed proactively');
              }
            } catch (error) {
              // Only log if still mounted (avoid logs after unmount)
              if (mounted) {
                console.error(
                  'Failed to refresh access token proactively:',
                  error,
                );
              }
            }
          }, 44 * 60 * 1000); // 44 minutes in milliseconds
        }
      } catch (error) {
        // Only log if still mounted
        if (mounted) {
          console.error('Failed to setup token refresh:', error);
        }
      }
    };

    // Setup refresh interval (async function - cleanup handles unmounting)
    setupRefresh();

    return () => {
      // Set mounted to false first to signal all async operations to stop
      mounted = false;
      if (refreshInterval) {
        clearInterval(refreshInterval);
        refreshInterval = null;
      }
    };
  }, [isHydrated, effectiveAuthState.accessToken]);

  const value: IAuth = React.useMemo(
    () => ({
      ...effectiveAuthState,
      // Only authenticate if we have a valid accessToken from server (cookies)
      // Don't rely on localStorage userProfile alone - it can be stale
      isAuthenticated: !!effectiveAuthState.accessToken,
      signout,
    }),
    [effectiveAuthState, signout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
